<template>
  <div class="position-relative">
    <label v-if="label" :for="inputId" class="form-label">
      <span
          v-if="btNumber"
          data-bs-toggle="tooltip"
          :data-bs-title="btTooltip"
          class="badge ehw-green"
      >
        {{ btNumber }}
      </span>
      {{ label }}
    </label>
    <div class="input-group">
      <input
          :required="required"
          ref="searchInput"
          :id="inputId"
          type="text"
          class="form-control"
          :class="{ 'is-invalid': this.hasParsingError}"
          :value="searchText"
          @input="onInput"
          @focus="onFocus"
          @blur="onBlur"
          @keydown.enter.prevent="onEnter"
          :placeholder="$t('ui.typeToSearch')"
      />
      <!-- Show internal X only if there's something to clear -->
      <button
          v-if="searchText"
          class="btn btn-outline-secondary clear-btn"
          @click="clearSelection"
          aria-label="Clear selection"
      >
        &times;
      </button>
    </div>
    <div
        v-if="dropdownVisible && filteredOptions.length"
        class="dropdown-menu show w-100"
        style="max-height: 200px; overflow:auto;"
    >
      <button
          class="dropdown-item"
          v-for="option in filteredOptions"
          :key="option.id"
          @mousedown.prevent="selectOption(option)"
      >
        {{ option.name }} ({{ option.id }})
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: "SearchableDropdown",
  props: {
    options: {
      type: Array,
      required: true
    },
    modelValue: {
      type: String,
      required: true
    },
    label: {
      type: String,
      default: "",
      required: false
    },
    inputId: {
      type: String,
      default: "",
      required: true
    },
    btNumber: {
      type: String,
      default: "",
      required: false
    },
    btTooltip: {
      type: String,
      default: "",
      required: false
    },hasParsingError:{
      type: Boolean,
      default: false,
      required: false
    },
    required: {
      type: Boolean,
      default: false,
      required: false
    }
  },
  emits: ["update:modelValue", "apply-filters"],
  data() {
    return {
      searchText: "",
      dropdownVisible: false,
      selectedOption: null
    };
  },
  computed: {
    filteredOptions() {
      const searchLower = this.searchText.toLowerCase().trim();
      return this.options.filter(opt => {
        const nameMatch = opt.name.toLowerCase().includes(searchLower);
        const idMatch = opt.id.toLowerCase().includes(searchLower);
        const shortNameMatch =
            opt.shortName && opt.shortName.toLowerCase().includes(searchLower);

        return nameMatch || idMatch || shortNameMatch;
      });
    }
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(newVal) {
        const opt = this.options.find(o => o.id === newVal);
        this.selectedOption = opt || null;
        this.searchText = opt ? `${opt.name} (${opt.id})` : "";
      }
    }
  },
  methods: {
    onInput(e) {
      this.searchText = e.target.value;
      this.dropdownVisible = true;
    },
    onFocus() {
      this.searchText = "";
      this.dropdownVisible = true;
    },
    onBlur() {
      if (
          !this.selectedOption ||
          this.searchText.toLowerCase() !== `${this.selectedOption.name} (${this.selectedOption.id})`.toLowerCase()
      ) {
        this.searchText = this.selectedOption
            ? `${this.selectedOption.name} (${this.selectedOption.id})`
            : "";
      }
      setTimeout(() => {
        this.dropdownVisible = false;
      }, 200);
    },
    onEnter() {
      const exactMatch = this.filteredOptions.find(
          opt =>
              `${opt.name} (${opt.id})`.toLowerCase() ===
              this.searchText.toLowerCase()
      );
      if (exactMatch) {
        this.selectOption(exactMatch);
      } else {
        this.searchText = this.selectedOption
            ? `${this.selectedOption.name} (${this.selectedOption.id})`
            : "";
        this.dropdownVisible = false;
      }
    },
    selectOption(option) {
      this.selectedOption = option;
      this.searchText = `${option.name} (${option.id})`;
      this.$emit("update:modelValue", option.id);
      this.$emit("apply-filters");
      this.dropdownVisible = false;

      this.$nextTick(() => {
        if (this.$refs.searchInput) {
          this.$refs.searchInput.blur();
        }
      });
    },
    clearSelection() {
      this.selectedOption = null;
      this.searchText = "";
      this.$emit("update:modelValue", "");
      this.$emit("apply-filters");
      this.dropdownVisible = false;
    }
  }
};
</script>

<style scoped>
.form-label {
  display: flex;
  align-items: center;
  gap: 6px;
}

.dropdown-menu {
  position: absolute;
  top: 100%;
  left: 0;
  z-index: 1000;
}

.input-group {
  display: flex;
  align-items: center;
}

.clear-btn {
  border: none;
  background: none;
  font-size: 1.25rem;
  line-height: 1;
  cursor: pointer;
}

.is-invalid {
  border-color: #dc3545 !important;
}
</style>
